import { useEffect, useMemo, useState } from "react";
import "./AdminOccupancyModule.scss";
import { toast } from "react-toastify";
import { OccupancyStatisticsExport } from "../../../types/api";
import StatisticsFilterList from "../StatisticsFilterList/StatisticsFilterList";

import { createStatisticsFilterStore } from "../Stores/statisticsFilterStoreFactory";
import { ReportTableSection } from "../Tables/ReportTableSection";
import { MembertypeChart } from "../Charts/MembertypeChart";
import { GuestChart } from "../Charts/GuestChart";

interface AdminOccupancyModuleProps {
  memberTypeOccupancyData: OccupancyStatisticsExport;
  guestOccupancyData: OccupancyStatisticsExport;
  courtOccupancyData: OccupancyStatisticsExport;
  memberTypeFilterStore: ReturnType<typeof createStatisticsFilterStore>;
  guestTypeFilterStore: ReturnType<typeof createStatisticsFilterStore>;
  bookedCourtsFilterStore: ReturnType<typeof createStatisticsFilterStore>;
  startDate: string;
  endDate: string;
  selectedWeekdays: number[];
}

const AdminOccupancyModule = ({
  memberTypeOccupancyData,
  guestOccupancyData,
  courtOccupancyData,
  memberTypeFilterStore,
  guestTypeFilterStore,
  bookedCourtsFilterStore,
  startDate,
  endDate,
  selectedWeekdays,
}: AdminOccupancyModuleProps) => {
  function countWeekdaysFromIntervals(startDate: Date, endDate: Date) {
    const counts = {
      monday: 0,
      tuesday: 0,
      wednesday: 0,
      thursday: 0,
      friday: 0,
      saturday: 0,
      sunday: 0,
    };

    const start = new Date(
      Date.UTC(
        startDate.getUTCFullYear(),
        startDate.getUTCMonth(),
        startDate.getUTCDate()
      )
    );
    const end = new Date(
      Date.UTC(
        endDate.getUTCFullYear(),
        endDate.getUTCMonth(),
        endDate.getUTCDate()
      )
    );

    for (let d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
      const dayOfWeek = d.getUTCDay(); // 0 = Sunday
      const isoDay = dayOfWeek;

      if (selectedWeekdays.includes(isoDay)) {
        switch (isoDay) {
          case 1:
            counts.monday++;
            break;
          case 2:
            counts.tuesday++;
            break;
          case 3:
            counts.wednesday++;
            break;
          case 4:
            counts.thursday++;
            break;
          case 5:
            counts.friday++;
            break;
          case 6:
            counts.saturday++;
            break;
          case 0:
            counts.sunday++;
            break;
        }
      }
    }

    return counts;
  }

  const [weekdayCounts, setWeekdayCounts] = useState({
    monday: 0,
    tuesday: 0,
    wednesday: 0,
    thursday: 0,
    friday: 0,
    saturday: 0,
    sunday: 0,
  });

  const totalDays = Object.values(weekdayCounts).reduce(
    (acc, count) => acc + count,
    0
  );
  useEffect(() => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const counts = countWeekdaysFromIntervals(start, end);
    setWeekdayCounts(counts);
  }, [startDate, endDate, selectedWeekdays]);
  const [chartType, setChartType] = useState<"line" | "pie">("line");

  return (
    <div className="admin-occupancy-module">
      <StatisticsFilterList
        useStore={memberTypeFilterStore}
        useTimeintervals={true}
        useTypeOfBookings={true}
        usePermissions={true}
        useCourts={true}
        useGender={true}
        useAgeGroups={true}
      />
      <ReportTableSection
        title="Beläggning - Medlemskap"
        columns={[
          { key: "memberType", label: "Medlemstyp" },
          { key: "year", label: "År" },
          { key: "week", label: "Vecka", align: "left" },
          {
            key: "monday",
            label: `Måndag (${weekdayCounts.monday})`,
            align: "left",
          },
          {
            key: "tuesday",
            label: `Tisdag (${weekdayCounts.tuesday})`,
            align: "left",
          },
          {
            key: "wednesday",
            label: `Onsdag (${weekdayCounts.wednesday})`,
            align: "left",
          },
          {
            key: "thursday",
            label: `Torsdag (${weekdayCounts.thursday})`,
            align: "left",
          },
          {
            key: "friday",
            label: `Fredag (${weekdayCounts.friday})`,
            align: "left",
          },
          {
            key: "saturday",
            label: `Lördag (${weekdayCounts.saturday})`,
            align: "left",
          },
          {
            key: "sunday",
            label: `Söndag (${weekdayCounts.sunday})`,
            align: "left",
          },
          {
            key: "total",
            label: `Total (${totalDays})`,
            align: "left",
          },
        ]}
        rows={memberTypeOccupancyData && memberTypeOccupancyData.data}
        useCalcType={false}
        useToggleType={true}
        onToggleType={(isPie: boolean) => setChartType(isPie ? "pie" : "line")}
        useStore={memberTypeFilterStore}
        isLoading={false}
      />
      <div className="bookings-chart-container text-m">
        <MembertypeChart
          type={chartType}
          rows={memberTypeOccupancyData && memberTypeOccupancyData.data}
          startDate={startDate}
          endDate={endDate}
        />
      </div>

      <StatisticsFilterList
        useStore={guestTypeFilterStore}
        useTimeintervals={true}
        usePermissions={true}
        useCourts={true}
        useGender={true}
        useAgeGroups={true}
      />
      <ReportTableSection
        title="Beläggning - Gäst"
        columns={[
          { key: "memberType", label: "Medlemstyp" },
          { key: "year", label: "År" },
          { key: "week", label: "Vecka", align: "left" },
          {
            key: "monday",
            label: `Måndag (${weekdayCounts.monday})`,
            align: "left",
          },
          {
            key: "tuesday",
            label: `Tisdag (${weekdayCounts.tuesday})`,
            align: "left",
          },
          {
            key: "wednesday",
            label: `Onsdag (${weekdayCounts.wednesday})`,
            align: "left",
          },
          {
            key: "thursday",
            label: `Torsdag (${weekdayCounts.thursday})`,
            align: "left",
          },
          {
            key: "friday",
            label: `Fredag (${weekdayCounts.friday})`,
            align: "left",
          },
          {
            key: "saturday",
            label: `Lördag (${weekdayCounts.saturday})`,
            align: "left",
          },
          {
            key: "sunday",
            label: `Söndag (${weekdayCounts.sunday})`,
            align: "left",
          },
          {
            key: "total",
            label: `Total (${totalDays})`,
            align: "left",
          },
        ]}
        rows={guestOccupancyData && guestOccupancyData.data}
        useCalcType={false}
        useStore={guestTypeFilterStore}
        isLoading={false}
      />
      <div className="bookings-chart-container text-m">
        <GuestChart
          rows={guestOccupancyData && guestOccupancyData.data}
          startDate={startDate}
          endDate={endDate}
        />
      </div>

      <StatisticsFilterList
        useStore={bookedCourtsFilterStore}
        useTimeintervals={true}
        useTypeOfBookings={true}
        usePermissions={true}
        useCourts={true}
        useGender={true}
        useAgeGroups={true}
        useActivityColors={true}
      />

      <ReportTableSection
        title="Beläggning - bokade banor"
        columns={[
          { key: "aktuellt_datum", label: "Aktuellt datum" },
          { key: "utfall_23", label: "Utfall 23.00", align: "center" },
          { key: "utfall_09", label: "Utfall 09.00", align: "center" },
          { key: "one_day_09", label: "1 dag innan", align: "center" },
          { key: "two_days_09", label: "2 dagar innan", align: "center" },
          { key: "three_days_09", label: "3 dagar innan", align: "center" },
          { key: "four_days_09", label: "4 dagar innan", align: "center" },
          { key: "five_days_09", label: "5 dagar innan", align: "center" },
        ]}
        rows={courtOccupancyData && courtOccupancyData.data}
        useStore={bookedCourtsFilterStore}
        isLoading={false}
      />
      <div className="occupancy-spacer" />
    </div>
  );
};
export default AdminOccupancyModule;
